import React, { createContext, useState, useEffect, useCallback } from "react";
// NEW: use the same BASE_URL import you use in Login.jsx
import { BASE_URL } from "../Api/ApiUrls"; // <-- adjust this path

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [userId, setUserId] = useState(() => localStorage.getItem("user_id"));
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem("session_token"),
  );
  const [isGuest, setIsGuest] = useState(
    () => localStorage.getItem("isGuest") === "true",
  );

  /* NEW =====================
     FCM TOKEN (from mobile app)
  ===================== */
  const [fcmToken, setFcmToken] = useState(() => window.fcmToken || "");

  // The app sets window.fcmToken and fires this event when the token is
  // ready or changes (it can arrive after the page has already loaded).
  useEffect(() => {
    const onNativeData = (e) => {
      if (e.detail?.fcmToken) setFcmToken(e.detail.fcmToken);
    };
    window.addEventListener("appDataFromNative", onNativeData);
    return () => window.removeEventListener("appDataFromNative", onNativeData);
  }, []);

  // Save (or clear, with "") the token on the server for the logged-in user
  const saveFcmToken = useCallback((uid, session, token) => {
    return fetch(`${BASE_URL}/update-fcm-token/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session}`,
      },
      body: JSON.stringify({ user_id: uid, fcm_token: token }),
      keepalive: true, // still completes if the page is navigating away
    }).catch((err) => console.error("FCM token save failed:", err));
  }, []);

  // Covers: token arriving after login, token refresh, and an app reopen
  // with a saved session. Guests have no userId/sessionId, so they are skipped.
  useEffect(() => {
    if (window.ReactNativeWebView && userId && sessionId && fcmToken) {
      saveFcmToken(userId, sessionId, fcmToken);
    }
  }, [userId, sessionId, fcmToken, saveFcmToken]);

  /* =====================
     PERSIST STORAGE
  ===================== */
  useEffect(() => {
    userId
      ? localStorage.setItem("user_id", userId)
      : localStorage.removeItem("user_id");
  }, [userId]);

  useEffect(() => {
    sessionId
      ? localStorage.setItem("session_token", sessionId)
      : localStorage.removeItem("session_token");
  }, [sessionId]);

  useEffect(() => {
    localStorage.setItem("isGuest", isGuest);
  }, [isGuest]);

  /* =====================
     AUTH FUNCTIONS
  ===================== */
  const login = (id, session) => {
    setUserId(id);
    setSessionId(session);
    setIsGuest(false);
  };

  const guestLogin = () => {
    setUserId(null);
    setSessionId(null);
    setIsGuest(true);
  };

  const logout = () => {
    console.log("🚪 Logging out...");

    // NEW: stop pushes to this user. Must run BEFORE storage is cleared.
    if (window.ReactNativeWebView && userId && sessionId) {
      saveFcmToken(userId, sessionId, "");
    }

    // ✅ Clear browser storage
    localStorage.clear();

    setUserId(null);
    setSessionId(null);
    setIsGuest(false);

    // ✅ 🔥 INFORM REACT NATIVE APP (NEW: typed message, so the app can
    // delete the device token and get a fresh one for the next user)
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "LOGOUT" }));
    }

    console.log("✅ Logout complete");
  };

  return (
    <AuthContext.Provider
      value={{ userId, sessionId, isGuest, login, guestLogin, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;